import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface ForgeLoaderProps {
  open: boolean;
}

const FORGE_STEPS = [
  'Parsing trade history...',
  'Extracting patterns...',
  'Identifying entry rules...',
  'Analyzing exit conditions...',
  'Forging strategy...',
  'Generating rules...',
];

export function ForgeLoader({ open }: ForgeLoaderProps) {
  const [stepIndex, setStepIndex] = useState(0);

  // Cycle through progress steps
  useEffect(() => {
    if (!open) {
      setStepIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setStepIndex((prev) => Math.min(prev + 1, FORGE_STEPS.length - 1));
    }, 2500);
    return () => clearInterval(interval);
  }, [open]);

  // Body scroll lock
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      role="dialog"
      aria-label="Analyzing trade history"
    >
      <div className="flex flex-col items-center gap-6 px-8">
        {/* Spinner */}
        <div className="w-14 h-14 rounded-full border-[3px] border-orange/20 border-t-orange animate-spin" />

        {/* Progress text */}
        <p
          className="text-sm font-bold text-orange"
          style={{ animation: 'forge-text-pulse 2s ease-in-out infinite' }}
        >
          {FORGE_STEPS[stepIndex]}
        </p>

        {/* Step progress dots */}
        <div className="flex gap-1.5">
          {FORGE_STEPS.map((_, i) => (
            <div
              key={i}
              className={`w-1.5 h-1.5 rounded-full transition-colors duration-300 ${
                i <= stepIndex ? 'bg-orange' : 'bg-ink-4'
              }`}
            />
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
